import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Lock, AlertCircle, CheckCircle2 } from "lucide-react";
import { t } from "@/lib/i18n";

export default function ChangePasswordModal() {
  const { changePassword } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) {
      setError(t("err_password_short"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("err_confirm_mismatch"));
      return;
    }
    setLoading(true);
    const { error } = await changePassword(newPassword);
    setLoading(false);
    if (error) {
      setError(t("err_change_password"));
    } else {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-5">
        <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-xl text-center">
          <CheckCircle2 className="w-16 h-16 text-[#2E7D32] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#1C3988] mb-2">{t("pw_changed_title")}</h2>
          <p className="text-gray-500 mb-6">{t("pw_changed_desc")}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#1C3988] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#122554] cursor-pointer"
          >
            {t("btn_continue")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-5">
      <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
            <Lock className="w-6 h-6 text-[#D97706]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#1C3988]">{t("pw_change_title")}</h2>
            <p className="text-sm text-gray-500">{t("pw_change_desc")}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-5">
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-sm text-[#1C3988]">{t("lbl_new_password")}</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-3 border border-[#CBD5E1] rounded-md text-sm outline-none focus:border-[#38B6FF] focus:shadow-[0_0_5px_rgba(56,182,255,0.4)] transition-all"
              placeholder={t("ph_new_password")}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-sm text-[#1C3988]">{t("lbl_confirm_new_password")}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-3 border border-[#CBD5E1] rounded-md text-sm outline-none focus:border-[#38B6FF] focus:shadow-[0_0_5px_rgba(56,182,255,0.4)] transition-all"
              placeholder={t("ph_confirm_new_password")}
            />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-[#D32F2F] bg-red-50 p-3 rounded-md text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="bg-[#1C3988] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#122554] flex items-center justify-center gap-2 cursor-pointer transition-all disabled:opacity-60"
          >
            {loading ? t("btn_changing") : t("btn_change_password")}
          </button>
        </form>
      </div>
    </div>
  );
}
